import { Injectable } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject, uploadBytesResumable } from '@angular/fire/storage';
import { Auth } from '@angular/fire/auth';
import { Observable, Subject, from, throwError, of } from 'rxjs';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { finalize, catchError, map, shareReplay } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

export interface UploadProgress {
  progress: number;
  url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  // Cache for download URLs
  private urlCache = new Map<string, { url: string; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private videoUrlCache: Map<string, string> = new Map();

  constructor(
    private storage: Storage,
    private auth: Auth,
    private angularFireStorage: AngularFireStorage
  ) {}

  uploadProductImage(file: File, path: string): Observable<UploadProgress> {
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Observable<UploadProgress>(observer => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next({ progress });
        },
        (error) => {
          console.error('Upload error:', error);
          observer.error(error);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            observer.next({ progress: 100, url });
            observer.complete();
          } catch (error) {
            console.error('Error getting download URL:', error);
            observer.error(error);
          }
        }
      );
    });
  }

  async uploadImage(file: File, path: string): Promise<string> {
    if (!this.auth.currentUser) {
      console.error('No authenticated user found');
      throw new Error('User must be authenticated to upload images');
    }

    try {
      const storageRef = ref(this.storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    } catch (error) {
      console.error('Error in uploadImage:', error);
      throw error;
    }
  }

  async deleteImage(path: string): Promise<void> {
    const storageRef = ref(this.storage, path);
    try {
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  generateUniqueFileName(file: File): string {
    const extension = file.name.split('.').pop();
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomString}.${extension}`;
  }

  async uploadVideo(file: File, onProgress: (progress: number) => void): Promise<string> {
    const timestamp = new Date().getTime();
    const filePath = `videos/${timestamp}_${file.name}`;
    const storageRef = ref(this.storage, filePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            console.error('Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  }

  /**
   * Uploads a raw video file to the transcoding pipeline
   * Files uploaded here will be automatically transcoded by Cloud Function
   * @param file Video file to upload
   * @param lessonId Lesson ID for organizing files
   * @param onProgress Progress callback
   * @returns Promise that resolves with the storage path (not download URL)
   */
  async uploadRawVideoForTranscoding(
    file: File,
    lessonId: string,
    onProgress: (progress: number) => void
  ): Promise<string> {
    const filePath = `raw-videos/${lessonId}/source.mp4`;
    const storageRef = ref(this.storage, filePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            // Return the storage path, not download URL
            // The Cloud Function will handle transcoding and generate URLs
            resolve(filePath);
          } catch (error) {
            console.error('Error completing upload:', error);
            reject(error);
          }
        }
      );
    });
  }

  uploadFile(path: string, file: File): Promise<string> {
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        () => {},
        (error) => reject(error),
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  deleteFile(path: string): Promise<void> {
    return this.angularFireStorage.ref(path).delete().toPromise();
  }

  /**
   * Deletes a file from Firebase Storage
   * Accepts either a full download URL or a clean storage path
   * @param urlOrPath Full download URL or storage path
   * @returns Promise that resolves when deletion is complete
   */
  async deleteFileByUrl(urlOrPath: string): Promise<void> {
    try {
      const cleanPath = this.cleanStoragePath(urlOrPath);
      const storageRef = ref(this.storage, cleanPath);
      
      await deleteObject(storageRef);
      
      // Remove from cache if exists
      this.urlCache.delete(cleanPath);
      this.videoUrlCache.delete(cleanPath);
      
      console.log(`Successfully deleted file: ${cleanPath}`);
    } catch (error: any) {
      // If file doesn't exist, that's okay - don't throw
      if (error?.code === 'storage/object-not-found') {
        console.warn(`File not found (may already be deleted): ${urlOrPath}`);
        return;
      }
      console.error(`Error deleting file ${urlOrPath}:`, error);
      throw error;
    }
  }

  /**
   * Deletes multiple files from Firebase Storage
   * @param urlsOrPaths Array of download URLs or storage paths
   * @returns Promise that resolves when all deletions are complete
   */
  async deleteFiles(urlsOrPaths: string[]): Promise<void> {
    const deletePromises = urlsOrPaths.map(urlOrPath => 
      this.deleteFileByUrl(urlOrPath).catch(error => {
        console.error(`Failed to delete ${urlOrPath}:`, error);
        // Continue with other deletions even if one fails
        return null;
      })
    );
    
    await Promise.all(deletePromises);
  }

  /**
   * Deletes all files in a directory (prefix) from Firebase Storage
   * Note: This requires listing files, which may need admin SDK
   * For now, this is a placeholder - actual implementation may require Cloud Function
   * @param prefix Storage path prefix (e.g., 'videos/lesson123/')
   */
  async deleteDirectory(prefix: string): Promise<void> {
    // This would require admin SDK or Cloud Function
    // For now, we'll handle it by deleting known files
    console.warn(`deleteDirectory called for ${prefix} - requires Cloud Function for full implementation`);
    throw new Error('Directory deletion requires Cloud Function - use deleteFiles() with specific paths instead');
  }

  getDownloadURL(path: string): Observable<string> {
    try {
      // Clean the path by removing any URL parameters and decoding it
      const cleanPath = this.cleanStoragePath(path);
      
      // Check cache first
      const cached = this.urlCache.get(cleanPath);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return of(cached.url);
      }

      // Get fresh URL
      const storageRef = ref(this.storage, cleanPath);
      return from(getDownloadURL(storageRef)).pipe(
        map(url => {
          // Cache the URL
          this.urlCache.set(cleanPath, {
            url,
            timestamp: Date.now()
          });
          return url;
        }),
        catchError(error => {
          console.error('Error getting download URL:', {
            error,
            path: cleanPath,
            originalPath: path
          });
          
          if (error.code === 'storage/object-not-found') {
            return throwError(() => new Error('Die angeforderte Datei wurde nicht gefunden.'));
          }
          return throwError(() => new Error('Fehler beim Laden der Datei. Bitte versuchen Sie es später erneut.'));
        }),
        shareReplay(1)
      );
    } catch (error) {
      return throwError(() => new Error('Ungültiger Dateipfad.'));
    }
  }

  private cleanStoragePath(path: string): string {
    try {
      // If it's a full Firebase Storage URL, extract the path
      if (path.includes('firebase') && path.includes('/o/')) {
        path = decodeURIComponent(path.split('/o/')[1].split('?')[0]);
      }
      
      // Remove any query parameters
      path = path.split('?')[0];
      
      // Decode the path
      return decodeURIComponent(path);
    } catch (error) {
      console.error('Error cleaning storage path:', error);
      return path;
    }
  }

  async downloadFile(url: string, filename: string): Promise<void> {
    try {
      // Extract the storage path from the URL if it's a Firebase Storage URL
      let path = url;
      if (url.includes('firebase') && url.includes('/o/')) {
        path = decodeURIComponent(url.split('/o/')[1].split('?')[0]);
      }

      // Get a fresh download URL
      const downloadURL = await firstValueFrom(this.getDownloadURL(path));
      
      // Create a temporary link and trigger the download
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw new Error('Fehler beim Herunterladen der Datei. Bitte versuchen Sie es später erneut.');
    }
  }

  /**
   * Get a download URL for a video in Firebase Storage
   * @param path Path to the video in storage (e.g., 'videos/guarantee-explanation.mp4')
   * @returns Observable with the download URL
   */
  getVideoUrl(path: string): Observable<string> {
    // Check if URL is already cached
    if (this.videoUrlCache.has(path)) {
      return of(this.videoUrlCache.get(path) as string);
    }

    // Get the reference to the file
    const videoRef = ref(this.storage, path);
    
    // Get the download URL and cache it
    return from(getDownloadURL(videoRef)).pipe(
      map(url => {
        this.videoUrlCache.set(path, url);
        return url;
      }),
      catchError(error => {
        console.error(`Error getting video URL for ${path}:`, error);
        throw error;
      })
    );
  }
} 